import styled from 'styled-components';
import {
  FormControl,
  FormLabel,
  Heading,
  Search,
  IconButton,
  Select,
  Option,
  Button,
} from '@alefeducation/ui-elements-library-core';
import {
  PlusCircleSolid,
  TrashSolid,
} from '@alefeducation/ui-elements-library-icons';
import React, { useState } from 'react';
import RolesSelection from './RolesSelection';
import { RoleSelection } from './roles.types';
import getCategoriesForSelectedRolesByOrg from './utils/CategoriesByRolesForOrg';
import { SearchPair } from './hooks/useRoles';
import useOrgRow from './hooks/userOrgRow';
import { Organisation } from './context/RolesContext';
import { Member } from '../../apollo/queries';
import { disableInput } from '../../styles/Common';
import {
  Role,
  RoleSelectionViewModel,
} from '../../apollo/queries/getAllRoles.queries';
import appLocalStorage from '../../utils/AppLocalStorage';

const leftSpace = () => `
  margin-left: 24px;
`;

const StyledRoles = styled.div`
  ${({ theme }) => `
     padding-top: ${theme.space[40]}px;
     padding-bottom: ${theme.space[28]}px;  
  `}
  .add-org-btn {
    ${leftSpace};
    padding-left: 6px;
  }
`;

const StyledHeading = styled(Heading)`
  ${leftSpace};
  margin-bottom: ${({ theme }: any) => `${theme.space[20]}px`};
`;

const StyledSearchContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  > div:first-child {
    flex-grow: 2;
  }
`;

const StyledRow = styled.div`
  display: flex;
  margin: ${({ theme }: any) => `${theme.space[20]}px 0`};
  ${leftSpace};
`;

const StyledFormControl = styled(FormControl)<{ disabled?: boolean }>`
  width: 40%;

  &:first-child {
    margin-right: ${({ theme }: any) => `${theme.space[28]}px`};
  }

  ${disableInput}
`;

const StyledFormControl2 = styled(FormControl)`
  display: flex;
  align-items: end;
  width: 20%;
  margin-left: ${({ theme }: any) => `${theme.space[16]}px`};
`;

const StyledSeparator = styled.div`
  height: 1px;
  width: 100%;
  background: #48546510;
`;

export interface RolesProps {
  isEdit?: boolean;
  disabled?: boolean;
  isOrgMember?: boolean;
  selectedRoles: RoleSelection[];
  user: Member | null;
  setSelectedOrgs?: (orgs: string[]) => void;

  setSelectedRoles(roles: any): void;
}

const Roles = ({
  setSelectedOrgs = () => {},
  disabled,
  selectedRoles,
  setSelectedRoles,
  isEdit = false,
  isOrgMember = false,
  user,
}: RolesProps) => {
  const {
    orgTabs,
    organisations,
    getRolesSelectionByOrg,
    selectedCategory,
    onCategorySelect,
    updateOrganisationForRow,
    onSearch,
    selectedRolesForSearch,
    onToggleSelectedRole,
    rolesForRoleSelection,
    onDeleteOrgRowClick,
    addOrganisationRow,
    getAllRoles,
  } = useOrgRow({
    setSelectedRoles,
    selectedRoles,
    user,
    isOrgMember,
    setSelectedOrgs,
  });

  const [searchText, setSearchText] = useState('');
  const [isToggleIconOpen, setIsToggleIcon] = useState(false);

  function renderRolesDropdown(org: any) {
    const { code } = org;
    const rolesSelection = getRolesSelectionByOrg(code);

    const selectedRolesCategories = getCategoriesForSelectedRolesByOrg({
      orgCode: code,
      roles: selectedRoles,
      roleSelectionViewModel: rolesSelection,
    });

    const options = [
      ...getAllRoles(rolesSelection, searchText).map((role) => ({
        key: role.id,
        value: role.name,
      })),
    ];

    const onInputFocus = (isFocused: boolean) => {
      if (isFocused && isToggleIconOpen) {
        setSearchText('');
        setIsToggleIcon(false);
      }
    };

    return (
      <StyledFormControl>
        <FormLabel>Choose Role</FormLabel>
        <StyledSearchContainer data-testid="select-roles-permissions">
          <Search
            onChange={(updatedRoles: SearchPair[]) => {
              onSearch(updatedRoles, code);
              setSearchText('');
            }}
            onInputChange={setSearchText}
            onInputFocus={onInputFocus}
            options={options}
            selectedOptions={selectedRolesForSearch(code)}
            onIconToggle={setIsToggleIcon}
            showToggleIcon
            selectOptionOnTab
            showOptionsOnInputFocus
          >
            {rolesSelection && !searchText.length && isToggleIconOpen && (
              <RolesSelection
                organisation={org}
                roleSelectionViewModel={rolesSelection}
                selectedRolesCategories={selectedRolesCategories}
                roles={rolesForRoleSelection(code)}
                onCategorySelect={onCategorySelect}
                selectedCategory={selectedCategory}
                onToggleSelectedRole={onToggleSelectedRole}
              />
            )}
          </Search>
        </StyledSearchContainer>
      </StyledFormControl>
    );
  }

  function renderOrganisationDropdown(orgTab: Organisation, idx: number) {
    return (
      <StyledFormControl
        disabled={!appLocalStorage.isViewOnlyRole() && isOrgMember}
      >
        <FormLabel data-testid={'member-name-label'}>
          Choose Organisation
        </FormLabel>
        <Select
          onSelectChange={() => {}}
          selectedOption={orgTab.code}
          data-testid="select-organisation"
          placeholder=""
          searchable
          id={`select-${orgTab.code}`}
        >
          {organisations.map((org2) => (
            <Option
              onClick={() => updateOrganisationForRow(org2.code, idx)}
              key={org2.code}
              value={org2.name}
            >
              {org2.name}
            </Option>
          ))}
        </Select>
      </StyledFormControl>
    );
  }

  function renderDeleteIcon(orgTab: Organisation, idx: number) {
    return (
      <StyledFormControl2>
        {!isOrgMember && (
          <IconButton
            size="large"
            tooltipContent="Delete organisation row"
            onClick={() => {
              onDeleteOrgRowClick(orgTab.code, idx);
            }}
          >
            <TrashSolid />
          </IconButton>
        )}
      </StyledFormControl2>
    );
  }

  function renderAddOrganisationButton() {
    const buttonText =
      orgTabs.length === 0 ? 'Add organisation' : 'Add another organisation';

    return (
      <Button
        className="add-org-btn"
        onClick={addOrganisationRow}
        variant="ghost"
        size="sm"
        icon={<PlusCircleSolid />}
      >
        {buttonText}
      </Button>
    );
  }

  return (
    <StyledRoles data-testid="roles-categories-view">
      <StyledHeading
        variant="heading-small"
        color={disabled ? 'primary.40' : 'text.100'}
        data-testid={'roles-and-permissions-heading'}
      >
        {`${isEdit ? '2' : '3'}. Roles and Permissions`}
      </StyledHeading>
      {!disabled && (
        <FormControl appearance="regular">
          {orgTabs.map((orgTab, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <StyledSeparator />}
              <StyledRow key={idx} data-testid="org-row">
                {renderOrganisationDropdown(orgTab, idx)}
                {renderRolesDropdown(orgTab)}
                {!appLocalStorage.isViewOnlyRole() &&
                  renderDeleteIcon(orgTab, idx)}
              </StyledRow>
            </React.Fragment>
          ))}
          {!isOrgMember &&
            !appLocalStorage.isViewOnlyRole() &&
            renderAddOrganisationButton()}
        </FormControl>
      )}
    </StyledRoles>
  );
};

export default Roles;
